// BCM-898 · Goods Receipt Dashboard — the "View Info" document icon.
//
// Opens the View Digitalized Information page directly (the document timeline that
// Batch Execution reaches through the "View Digitalized Information" button — those
// buttons are hidden on the GR eye view, so this icon is the way in).
// Sync Inventory / Sync PO / Resubmit Document are forwarded (as `extraActions`,
// a function of the open document's type) into the two-pane digitalization view
// below it — see CommonDigitizedDocument's header render.
import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { Dialog, IconButton, Tooltip, CircularProgress } from "@material-ui/core";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import CustomDialogTitle from "../../projectsDashboard/CustomDialogTitle";
import ViewStructuredInformation from "../../sasl/batch/ViewStructuredInformation";
import SyncActions from "./common/SyncActions";
import ResubmitDocumentButton from "./common/ResubmitDocumentButton";
import { fetchBatchDetails } from "../api/goodsReceiptApi";
import { useGRStyles, GR_FONT_FAMILY } from "../goodsReceiptStyles";

const ViewDigitalizedInfo = ({ row, featuresAccess, onRefresh }) => {
  const classes = useGRStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batchDetails, setBatchDetails] = useState(null);
  const cancelRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving view digitalized info");
    };
  }, []);

  const load = useCallback(() => {
    if (loading) return;
    setLoading(true);
    const source = axios.CancelToken.source();
    cancelRef.current = source;
    fetchBatchDetails(
      {
        schrockenDeliveryScheduleBatchID: row.schrockenDeliveryScheduleBatchID,
        poNumber: row.scheduleAgreementNumber,
        batchNumber: row.vendorBatchNumber,
      },
      source.token
    )
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.data && res.data.status) {
          setBatchDetails(res.data.data);
          setOpen(true);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        /* leave the dialog closed — the row stays usable */
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [loading, row]);

  // Changes made in this dialog (document review / field edits, allocation or
  // consumption updates) only mark the batch stale: refreshing the dashboard while
  // the dialog is open would unmount it (the table swaps every row for its loading
  // row), so the dashboard refreshes once, on close. Sync PO / Inventory keep
  // their own status poll + refresh; Resubmit Document needs no refresh.
  const staleRef = useRef(false);
  const markStale = useCallback(() => {
    staleRef.current = true;
  }, []);

  const handleClose = () => {
    setOpen(false);
    setBatchDetails(null);
    if (staleRef.current) {
      staleRef.current = false;
      if (onRefresh) onRefresh();
    }
  };

  // Sync buttons here must trigger the same reset as every other GR pending
  // action (PA2/PA3/PA4/PA6/PA8) — BCM-898: SAP sync ran but the batch reset
  // was never called because these were pinned to false.
  //
  // A function of the currently-open document's type, not a static node — this
  // page's document timeline mounts one CommonDigitizedDocument per document,
  // each calling extraActions with its own docType (see CommonDigitizedDocument's
  // header render), so Resubmit Document pre-targets whichever document the user
  // actually has open instead of always defaulting to the first fetched type.
  // Matches the headRight={(active) => ...} pattern PA2ReviewFields already uses.
  const actions = (activeDocType) => (
    <React.Fragment>
      <SyncActions
        row={row}
        detail={batchDetails}
        showSyncInventory
        showSyncPO
        resetsBatchInventory
        resetsBatchPO
        onRefresh={onRefresh}
      />
      <ResubmitDocumentButton
        row={row}
        detail={batchDetails}
        featuresAccess={featuresAccess}
        onAfter={onRefresh}
        activeDocType={activeDocType}
        blue
      />
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Tooltip title="View Digitalized Information">
        <span>
          <IconButton
            size="small"
            className={classes.grIconBtn}
            onClick={load}
            disabled={loading}
            aria-label="view digitalized information"
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DescriptionOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {open && batchDetails && (
        <Dialog
          open={open}
          onClose={handleClose}
          fullScreen
          PaperProps={{ style: { fontFamily: GR_FONT_FAMILY } }}
        >
          <CustomDialogTitle
            onBackClick={handleClose}
            title={"View Digitalized Information"}
          />
          <ViewStructuredInformation
            batchDetails={batchDetails}
            schrockenDeliveryScheduleID={row.schrockenDeliveryScheduleID}
            cmoBatchNumber={row.cmoBatchNumber || batchDetails.cmoBatchNumber}
            targetQuantity={row.targetQuantity || row.poQuantity || row.batchQty}
            consumptionSameAsAllocation={batchDetails.consumptionSameAsAllocation}
            extraActions={actions}
            onRefreshParent={markStale}
          />
        </Dialog>
      )}
    </React.Fragment>
  );
};

export default ViewDigitalizedInfo;
